import { useState } from "react"
import Container from "react-bootstrap/Container"
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom"

import ErrorAlert from "./components/ErrorAlert"
import SuccessAlert from "./components/SuccessAlert"
import TopBar, { ViewType } from "./components/TopBar"
import { AuthenticationContextProvider } from "./contexts/AuthenticationContext/AuthenticationContext.provider"
import { useAuthDispatch, useAuthState } from "./contexts/AuthenticationContext/AuthenticationContext.hooks"
import { CampaignProvider } from "./contexts/CampaignContext"
import { ErrorProvider, useError } from "./contexts/ErrorContext"
import { SuccessProvider } from "./contexts/SuccessContext"
import { CampaignsPage } from "./pages/authenticated/CampaignsPage"
import { CamerasPage } from "./pages/authenticated/CamerasPage"
import { RecordsPage } from "./pages/authenticated/RecordsPage"
import VideoCorruptionCheckPage from "./pages/authenticated/VideoCorruptionCheckPage"
import DebugPage from "./pages/DebugPage"
import LoginPage from "./pages/LoginPage"
import { logout as logoutApi } from "./services/auth"


// ── AuthGuard ─────────────────────────────────────────────────────────────────
// Replaces <ProtectedRoute>.  Reads authentication state from context instead
// of making a network round-trip on every render.

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isRefreshing } = useAuthState()
  const location = useLocation()

  if (isRefreshing) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}


// ── MainLayout ────────────────────────────────────────────────────────────────

function MainLayout() {
  const [currentView, setCurrentView] = useState<ViewType>("records")
  const { setError } = useError()
  const { logout } = useAuthDispatch()
  const { isAdmin } = useAuthState()

  const handleLogout = async () => {
    try {
      // Invalidate the server-side session.
      await logoutApi()
    } catch (error) {
      setError(`Logout failed: ${error}`)
    } finally {
      // Clear tokens from context + localStorage and navigate to /login.
      logout()
    }
  }

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view)
  }

  const renderMainContent = () => {
    switch (currentView) {
      case "cameras":
        return <CamerasPage />
      case "check_videos":
        return <VideoCorruptionCheckPage />
      case "campaigns":
        // Admin-only view; non-admins fall back to records (the backend also enforces this).
        return isAdmin ? <CampaignsPage /> : <RecordsPage />
      case "records":
      default:
        return <RecordsPage />
    }
  }

  return (
    <div className="App" style={{ height: "100vh", overflow: "hidden" }}>
      {/* Top Bar (Always Fixed) */}
      <TopBar
        handleLogout={handleLogout}
        currentView={currentView}
        onViewChange={handleViewChange}
        isAdmin={isAdmin}
      />
      {/* Main Layout */}
      <Container fluid className="px-0">
        <div style={{ height: "calc(100vh - 56px)" }}>
          {renderMainContent()}
        </div>
      </Container>
    </div>
  )
}


// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <ErrorProvider>
      <SuccessProvider>
        <ErrorAlert />
        <SuccessAlert />
        <Router>
          {/*
            AuthenticationContextProvider must live inside <Router> because
            the provider uses useNavigate() to redirect on logout/token expiry.
          */}
          <AuthenticationContextProvider>
            <Routes>
              <Route path="/debug" element={<DebugPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <AuthGuard>
                    <CampaignProvider>
                      <MainLayout />
                    </CampaignProvider>
                  </AuthGuard>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthenticationContextProvider>
        </Router>
      </SuccessProvider>
    </ErrorProvider>
  )
}
